import React, { useState, useEffect } from 'react';
import { cropAPI, translationAPI } from '../../services/api';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import useStore from '../../store/useStore';
import TranslationPanel from './TranslationPanel';
import QRCodeManager from './QRCodeManager';
import UtilsPanel from './UtilsPanel';
import './AdminDashboard.css';

const SeedManager = ({ view: initialView = 'list' }) => {
  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();
  const { logout, getImageUrl, userRole } = useStore();

  // Check if user has read-only access
  const isReadOnly = userRole === 'packing';

  // View state - list, form, or qrcode
  const [currentView, setCurrentView] = useState(initialView);

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchLimit, setSearchLimit] = useState(10);
  const [sortField, setSortField] = useState('id');
  const [sortDirection, setSortDirection] = useState('asc');
  const [searchMode, setSearchMode] = useState('query'); // 'query' or 'all'
  const [filterActive, setFilterActive] = useState('active'); // 'all', 'active', 'inactive'

  // Form state
  const [editingSeed, setEditingSeed] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [formData, setFormData] = useState({
    cropName: '',
    varietyCode: '',
    isActive: true,
    pouchImageName: ''
  });

  // Multilingual translations - JSONB format
  const [translations, setTranslations] = useState({
    cropNameMl: {},
    varietyNameMl: {},
    eligibleStatesMl: {},
    landPreparationMl: {},
    seedProcessingMl: {},
    sowingTimeMl: {},
    seedRateMl: {},
    fertilizersMl: {},
    weedControlMl: {},
    irrigationMl: {},
    harvestingMl: {}
  });

  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [formMessage, setFormMessage] = useState('');
  const [listMessage, setListMessage] = useState('');
  const [qrPreviewUrl, setQrPreviewUrl] = useState(null);
  const [showScrollHelp, setShowScrollHelp] = useState(false);
  const [showTranslationModal, setShowTranslationModal] = useState(false);
  const [showUtilsModal, setShowUtilsModal] = useState(false);
  const [translationStatus, setTranslationStatus] = useState('');
  const [isCheckingStatus, setIsCheckingStatus] = useState(false);
  const [isServiceRunning, setIsServiceRunning] = useState(false);
  const [serviceState, setServiceState] = useState({ activeState: 'unknown', subState: 'unknown' });

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Scroll functions
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const scrollToBottom = () => {
    window.scrollTo({ top: document.documentElement.scrollHeight, behavior: 'smooth' });
  };

  const scrollUp = () => {
    window.scrollBy({ top: -window.innerHeight * 0.8, behavior: 'smooth' });
  };

  const scrollDown = () => {
    window.scrollBy({ top: window.innerHeight * 0.8, behavior: 'smooth' });
  };

  // Keyboard shortcuts for scrolling
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Skip if user is typing in an input field
      const activeElement = document.activeElement;
      const isInputField = activeElement.tagName === 'INPUT' ||
        activeElement.tagName === 'TEXTAREA' ||
        activeElement.isContentEditable;

      if (isInputField) return;

      // Ctrl/Cmd + Home = Scroll to top
      if ((e.ctrlKey || e.metaKey) && e.key === 'Home') {
        e.preventDefault();
        scrollToTop();
      }
      // Ctrl/Cmd + End = Scroll to bottom
      else if ((e.ctrlKey || e.metaKey) && e.key === 'End') {
        e.preventDefault();
        scrollToBottom();
      }
      // Page Up = Scroll up
      else if (e.key === 'PageUp') {
        e.preventDefault();
        scrollUp();
      }
      // Page Down = Scroll down
      else if (e.key === 'PageDown') {
        e.preventDefault();
        scrollDown();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Sync view with route
  useEffect(() => {
    setCurrentView(initialView);

    // Handle edit route
    if (params.id && initialView === 'form') {
      const seedId = parseInt(params.id);
      fetchSeedForEdit(seedId);
    }

    // Handle QR route with variety code
    if (params.varietyCode && initialView === 'qrcode') {
      // QRCodeManager will handle loading the seed by variety code
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialView, params.id, params.varietyCode, location.pathname]);

  // Sorting function
  const sortResults = (results) => {
    if (!sortField) return results;

    return [...results].sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];

      // Handle null/undefined
      if (aVal == null) return 1;
      if (bVal == null) return -1;

      // Compare
      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return ' ⇅';
    return sortDirection === 'asc' ? ' ▲' : ' ▼';
  };

  const handleSearch = async () => {
    // If in query mode, require a search query
    if (searchMode === 'query' && !searchQuery.trim()) {
      setListMessage('Please enter a search query or select "All Records"');
      return;
    }

    try {
      setSearching(true);
      setListMessage('');

      // Determine query based on mode
      const query = searchMode === 'all' ? '' : searchQuery;

      // Determine isActive filter
      let isActiveParam = null;
      if (filterActive === 'active') {
        isActiveParam = true;
      } else if (filterActive === 'inactive') {
        isActiveParam = false;
      }

      const response = await cropAPI.searchCrops(query, 'en', searchLimit, isActiveParam);
      let results = response.data;

      setSearchResults(sortResults(results));
      if (results.length === 0) {
        setListMessage('No crops found');
      } 
      // else {
      //   setListMessage(`Found ${results.length} crop(s)`);
      // }
    } catch (error) {
      setListMessage(`Search failed: ${error.response?.data?.message || error.message}`);
    } finally {
      setSearching(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        setFormMessage('Please select a valid image file (JPG, PNG, or WebP)');
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        setFormMessage('File size must be less than 5MB');
        return;
      }

      setSelectedFile(file);
      setFormMessage('');
    }
  };

  const handleTranslationChange = (field, newTranslations) => {
    setTranslations(prev => ({
      ...prev,
      [field]: newTranslations
    }));
  };

  // Start translation batch process
  const handleStartTranslation = async () => {
    if (isServiceRunning) {
      setTranslationStatus('⚠️ Translation service is already running. Please wait for it to complete.');
      return;
    }

    try {
      const response = await translationAPI.startTranslation();
      setTranslationStatus(`✅ Translation started successfully at ${new Date(response.data.timestamp).toLocaleString()}`);

      // Check status after a brief delay to update the running state
      setTimeout(() => {
        handleCheckTranslationStatus();
      }, 1000);
    } catch (error) {
      console.error('Translation start error:', error);
      setTranslationStatus(`❌ Failed to start translation: ${error.response?.data?.error || error.message}`);
    }
  };

  // Check translation service status
  const handleCheckTranslationStatus = async () => {
    setIsCheckingStatus(true);
    try {
      const response = await translationAPI.getTranslationStatus();
      const { activeState, subState, log } = response.data;

      // Consider "active" and "activating" as running
      const isRunning = activeState === 'active' || activeState === 'activating';

      setIsServiceRunning(isRunning);
      setServiceState({ activeState, subState });

      // Display only the log content if available
      let statusMessage = '';
      if (log && log.trim()) {
        statusMessage = log;
      } else {
        // Fallback message if no log is available
        if (activeState === 'active' && subState === 'running') {
          statusMessage = '✅ Translation service is running (no log available yet)';
        } else if (activeState === 'activating') {
          statusMessage = '🔄 Translation service is starting...';
        } else if (activeState === 'failed') {
          statusMessage = '❌ Translation service failed';
        } else if (activeState === 'inactive') {
          statusMessage = '⚪ Translation service is not running';
        } else {
          statusMessage = '⚠️ Translation service status unknown';
        }
      }

      setTranslationStatus(statusMessage);
    } catch (error) {
      console.error('Translation status error:', error);
      setTranslationStatus(`❌ Failed to check status: ${error.response?.data?.error || error.message}`);
      setIsServiceRunning(false);
      setServiceState({ activeState: 'unknown', subState: 'unknown' });
    } finally {
      setIsCheckingStatus(false);
    }
  };

  // Auto-check status when modal opens
  useEffect(() => {
    if (showTranslationModal) {
      handleCheckTranslationStatus();
    }
  }, [showTranslationModal]);

  const startCreating = () => {
    setEditingSeed(null);
    setIsCreating(true);
    setFormMessage('');
    setFormData({
      cropName: '',
      varietyCode: '',
      isActive: true,
      pouchImageName: ''
    });
    setTranslations({
      cropNameMl: {},
      varietyNameMl: {},
      eligibleStatesMl: {},
      landPreparationMl: {},
      seedProcessingMl: {},
      sowingTimeMl: {},
      seedRateMl: {},
      fertilizersMl: {},
      weedControlMl: {},
      irrigationMl: {},
      harvestingMl: {}
    });
    navigate('/seeds/create');
  };

  const fetchSeedForEdit = async (seedId) => {
    try {
      const response = await cropAPI.getCrop(seedId);
      const seed = response.data;

      setEditingSeed(seed);
      setIsCreating(false);
      setFormData({
        cropName: seed.cropName || '',
        varietyCode: seed.varietyCode || '',
        isActive: seed.isActive !== undefined ? seed.isActive : true,
        pouchImageName: seed.pouchImageName || ''
      });

      setTranslations({
        cropNameMl: seed.cropNameMl || {},
        varietyNameMl: seed.varietyNameMl || {},
        eligibleStatesMl: seed.eligibleStatesMl || {},
        landPreparationMl: seed.landPreparationMl || {},
        seedProcessingMl: seed.seedProcessingMl || {},
        sowingTimeMl: seed.sowingTimeMl || {},
        seedRateMl: seed.seedRateMl || {},
        fertilizersMl: seed.fertilizersMl || {},
        weedControlMl: seed.weedControlMl || {},
        irrigationMl: seed.irrigationMl || {},
        harvestingMl: seed.harvestingMl || {}
      });

      if (seed.varietyCode) {
        const qrResponse = await fetch(`${API_BASE_URL}/api/qr/${encodeURIComponent(seed.varietyCode)}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        if (qrResponse.ok) {
          const blob = await qrResponse.blob();
          const objectUrl = URL.createObjectURL(blob);
          setQrPreviewUrl(objectUrl);
        } else {
          setQrPreviewUrl(null);
        }
      }
    } catch (error) {
      setFormMessage(`Failed to load crop: ${error.response?.data?.message || error.message}`);
      navigate('/seeds');
    }
  };

  const startEditing = async (seed) => {
    try {
      setFormMessage('Loading crop data...');
      const response = await cropAPI.getCrop(seed.id);
      const fullCrop = response.data;

      setIsCreating(false);
      setEditingSeed(fullCrop);
      setFormMessage('');

      setFormData({
        cropName: fullCrop.cropName || '',
        varietyCode: fullCrop.varietyCode || '',
        isActive: fullCrop.isActive,
        pouchImageName: fullCrop.pouchImageName || ''
      });

      setTranslations({
        cropNameMl: fullCrop.cropNameMl || {},
        varietyNameMl: fullCrop.varietyNameMl || {},
        eligibleStatesMl: fullCrop.eligibleStatesMl || {},
        landPreparationMl: fullCrop.landPreparationMl || {},
        seedProcessingMl: fullCrop.seedProcessingMl || {},
        sowingTimeMl: fullCrop.sowingTimeMl || {},
        seedRateMl: fullCrop.seedRateMl || {},
        fertilizersMl: fullCrop.fertilizersMl || {},
        weedControlMl: fullCrop.weedControlMl || {},
        irrigationMl: fullCrop.irrigationMl || {},
        harvestingMl: fullCrop.harvestingMl || {}
      });

      // Load QR code preview with auth token
      const token = localStorage.getItem('authToken');
      const qrUrl = `${API_BASE_URL}/QrCode/${encodeURIComponent(fullCrop.varietyCode)}/preview?size=200&format=PNG`;

      // Create authenticated image URL
      const qrResponse = await fetch(qrUrl, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (qrResponse.ok) {
        const blob = await qrResponse.blob();
        const objectUrl = URL.createObjectURL(blob);
        setQrPreviewUrl(objectUrl);
      } else {
        setQrPreviewUrl(null);
      }

      navigate(`/seeds/edit/${seed.id}`);
    } catch (error) {
      setListMessage(`Failed to load crop: ${error.response?.data?.message || error.message}`);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.cropName || !formData.varietyCode) {
      setFormMessage('Crop name and variety code are required');
      return;
    }

    try {
      setUploading(true);
      setFormMessage('');

      let result;
      let seedId;

      // Step 1: Upload image first if there's a file selected
      if (selectedFile) {
        if (isCreating) {
          // For new crops, we need to create the crop first to get an ID
          const tempApiData = {
            cropName: formData.cropName,
            varietyCode: formData.varietyCode,
            pouchImageName: null,
            isActive: formData.isActive,
            ...translations
          };
          result = await cropAPI.insertCrop(tempApiData);
          seedId = result.data.id;
        } else {
          seedId = editingSeed.id;
        }

        // Upload the image
        const uploadResult = await cropAPI.uploadPouchImage(seedId, selectedFile);
        setFormMessage(`Image uploaded! 📷`);

        // Update formData with the new image name returned from upload
        if (uploadResult.data && uploadResult.data.fileName) {
          formData.pouchImageName = uploadResult.data.fileName;
        }
      }

      // Step 2: Save/update crop data
      if (isCreating) {
        if (!result) {
          // No file was uploaded, create the crop now
          const apiData = {
            cropName: formData.cropName,
            varietyCode: formData.varietyCode,
            pouchImageName: formData.pouchImageName || null,
            isActive: formData.isActive,
            ...translations
          };
          result = await cropAPI.insertCrop(apiData);
        } else {
          // Crop was already created for image upload, now update with translations
          const apiData = {
            pouchImageName: formData.pouchImageName || null,
            isActive: formData.isActive,
            ...translations
          };
          await cropAPI.updateCrop(seedId, apiData);
        }
        setFormMessage(`Seed created successfully! ✅`);
      } else {
        // For updates, only send translations and isActive
        // Do NOT send pouchImageName unless it was explicitly changed
        const apiData = {
          isActive: formData.isActive,
          ...translations
        };
        // Only include pouchImageName if a new file was uploaded
        if (selectedFile && formData.pouchImageName) {
          apiData.pouchImageName = formData.pouchImageName;
        }
        result = await cropAPI.updateCrop(editingSeed.id, apiData);
        setFormMessage(`Seed updated successfully! ✅`);
      }

      setSelectedFile(null);

      // Reset file input
      const fileInput = document.getElementById('imageFile');
      if (fileInput) fileInput.value = '';

    } catch (error) {
      setFormMessage(`Failed to ${isCreating ? 'create' : 'update'} seed: ${error.response?.data || error.message}`);
    } finally {
      setUploading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      cropName: '',
      varietyCode: '',
      isActive: true,
      pouchImageName: ''
    });
    setTranslations({
      cropNameMl: {},
      varietyNameMl: {},
      eligibleStatesMl: {},
      landPreparationMl: {},
      seedProcessingMl: {},
      sowingTimeMl: {},
      seedRateMl: {},
      fertilizersMl: {},
      weedControlMl: {},
      irrigationMl: {},
      harvestingMl: {}
    });
  };

  const handleDelete = async (seedId, currentlyActive) => {
    const action = currentlyActive ? 'Inactive' : 'restore';
    if (!window.confirm(`Are you sure you want to ${action} this seed?`)) {
      return;
    }

    try {
      setListMessage('');
      await cropAPI.deleteCrop(seedId, currentlyActive ? 0 : 1);
      setListMessage(`Seed ${action}d successfully! ✅`);

      // Refresh search results
   
        handleSearch();
      
    } catch (error) {
      setListMessage(`Failed to ${action} seed: ${error.response?.data?.message || error.message}`);
    }
  };

  const cancelForm = () => {
    setEditingSeed(null);
    setIsCreating(false);
    setSelectedFile(null);
    setFormMessage('');
    navigate('/seeds');

    // Clean up QR preview URL
    if (qrPreviewUrl) {
      URL.revokeObjectURL(qrPreviewUrl);
      setQrPreviewUrl(null);
    }

    resetForm();

    const fileInput = document.getElementById('imageFile');
    if (fileInput) fileInput.value = '';
  };

  const backToList = () => {
    setFormMessage('');

    // Clean up QR preview URL
    if (qrPreviewUrl) {
      URL.revokeObjectURL(qrPreviewUrl);
      setQrPreviewUrl(null);
    }

    navigate('/seeds');
  };

  const openQRManager = () => {
    navigate('/qr');
  };

  const downloadQRCode = async (varietyCode) => {
    try {
      setListMessage('Downloading QR code...');

      // Get auth token
      const token = localStorage.getItem('authToken');

      // Fetch the QR code as PNG with authentication (print quality)
      const response = await fetch(`${API_BASE_URL}/QrCode/${encodeURIComponent(varietyCode)}?size=300&format=PNG&includeBranding=true`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to download QR code');
      }

      // Get the blob
      const blob = await response.blob();

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `QR_${varietyCode}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setListMessage('QR code downloaded successfully! ✅');
      setTimeout(() => setListMessage(''), 3000);
    } catch (error) {
      setListMessage(`Failed to download QR code: ${error.message}`);
    }
  };

  // Display results are already filtered by the API based on filterActive
  const displayResults = sortResults(searchResults);

  return (
    <div className="admin-dashboard">
      {/* Header */}
      <header className="admin-header">
        <div className="header-left">
          <div className="logo">
            <div className="ns-logo">
              <img src="/logo.png" alt="Namdhari Seeds Logo" className="w-10 h-10" />
            </div>
            <div className="company-info">
              <span className="company-name">Namdhari Seeds</span>
              <span className="admin-text">Seeds for a better future</span>
            </div>
          </div>
        </div>


        <div className="header-right">
          <button
            className="nav-button"
            onClick={() => navigate('/seeds')}
            disabled={currentView === 'list'}
          >
            📋 Seed List
          </button>
          <button
            className="nav-button"
            onClick={openQRManager}
            disabled={currentView === 'qrcode'}
          >
            🔲 QR Codes
          </button>
          {!isReadOnly && (
            <button
              className="nav-button"
              onClick={() => setShowTranslationModal(true)}
            >
              🌍 Translation
            </button>
          )}
          {!isReadOnly && (
            <button
              className="nav-button"
              onClick={() => setShowUtilsModal(true)}
            >
              🛠️ Utils
            </button>
          )}
          <button className="logout-button" onClick={handleLogout}>
            Logout
          </button>
        </div>
      </header>

      <div className="seed-manager" style={{ padding: '20px' }}>
        {/* QR Code Manager View */}
        {currentView === 'qrcode' && (
          <QRCodeManager seeds={searchResults.filter(s => s.isActive)} />
        )}

        {/* List View */}
        {currentView === 'list' && (
          <>
            <div className="section-header">
              <h1>Seed Management</h1>
              {!isReadOnly && (
                <button className="refresh-button" onClick={startCreating}>
                  ➕ Add New Seed
                </button>
              )}
            </div>

            {listMessage && (
              <div className={`message ${listMessage.includes('success') || listMessage.includes('✅') ? 'success' : 'error'}`}>
                {listMessage}
              </div>
            )}

            {/* Search Section */}
            <div className="search-section" style={{ marginBottom: '30px' }}>
              <h2>Search Seeds</h2>

              {/* Search Mode Selection */}
              <div style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <input
                    type="radio"
                    value="query"
                    checked={searchMode === 'query'}
                    onChange={(e) => setSearchMode(e.target.value)}
                  />
                  Search by Query
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <input
                    type="radio"
                    value="all"
                    checked={searchMode === 'all'}
                    onChange={(e) => setSearchMode(e.target.value)}
                  />
                  All Records
                </label>
              </div>

              {/* Search Input and Filters */}
              <div style={{ display: 'flex', gap: '10px', marginBottom: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                <input
                  type="text"
                  placeholder="Search by crop name, variety code..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  disabled={searchMode === 'all'}
                  style={{
                    flex: 1,
                    minWidth: '250px',
                    padding: '10px',
                    fontSize: '16px',
                    opacity: searchMode === 'all' ? 0.5 : 1
                  }}
                />

                <select
                  value={filterActive}
                  onChange={(e) => setFilterActive(e.target.value)}
                  style={{ padding: '10px', fontSize: '16px' }}
                  title="Filter by status"
                >
                  <option value="all">All Status</option>
                  <option value="active">Active Only</option>
                  <option value="inactive">Inactive Only</option>
                </select>

                <select
                  value={searchLimit}
                  onChange={(e) => setSearchLimit(Number(e.target.value))}
                  style={{ padding: '10px', fontSize: '16px' }}
                  title="Results limit"
                >
                  <option value="10">10 results</option>
                  <option value="25">25 results</option>
                  <option value="50">50 results</option>
                  <option value="100">100 results</option>
                  <option value="500">500 results</option>
                  <option value="1000">1000 results</option>
                </select>

                <button
                  onClick={handleSearch}
                  disabled={searching}
                  className="upload-button"
                  style={{ whiteSpace: 'nowrap' }}
                >
                  {searching ? '⏳ Searching...' : '🔍 Search'}
                </button>
              </div>

              {/* Search Results */}
              {searchResults.length > 0 && (
                <div className="crops-list">
                  <h3>Total : {displayResults.length} Record(s) Found</h3>
                  <div className="crops-table">
                    <div className="table-header">
                      <div onClick={() => handleSort('id')} style={{ cursor: 'pointer' }}>
                        ID{getSortIcon('id')}
                      </div>
                      <div onClick={() => handleSort('varietyCode')} style={{ cursor: 'pointer' }}>
                        Variety Code{getSortIcon('varietyCode')}
                      </div>
                      <div onClick={() => handleSort('cropName')} style={{ cursor: 'pointer' }}>
                        Seed Name{getSortIcon('cropName')}
                      </div>
                      <div onClick={() => handleSort('isActive')} style={{ cursor: 'pointer' }}>
                        Status{getSortIcon('isActive')}
                      </div>
                      <div>Image</div>
                      <div>Variety URL</div>
                      <div>Actions</div>
                    </div>

                    {displayResults.map(seed => (
                      <div key={seed.id} className={`table-row ${!seed.isActive ? 'deleted-row' : ''}`}>
                        <div>{seed.id}</div>
                        <div className="variety-code">{seed.varietyCode}</div>
                        <div>{seed.cropName}</div>
                        <div className={`status ${seed.isActive ? 'active' : 'inactive'}`}>
                          {seed.isActive ? 'Active' : 'Inactive'}
                        </div>
                        <div className={`image-status ${seed.pouchImageName ? 'has-image' : 'no-image'}`}>
                          {seed.pouchImageName ? (
                            <img
                              src={getImageUrl(seed.pouchImageName)}
                              alt={seed.cropName}
                              style={{ width: '40px', height: '40px', objectFit: 'cover', borderRadius: '4px' }}
                            />
                          ) : (
                            '✗ No Image'
                          )}
                        </div>
                        <div>
                          <a
                            href={`/variety/${encodeURIComponent(seed.varietyCode)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: '#2d5016', textDecoration: 'underline', fontSize: '14px' }}
                          >
                            🔗 View Page
                          </a>
                        </div>
                        <div className="actions">
                          <button
                            onClick={() => startEditing(seed)}
                            className="action-button view"
                            style={{ border: 'none', cursor: 'pointer' }}
                          >
                            {isReadOnly ? '👁️ View' : '✏️ Edit'}
                          </button>
                          <button
                            onClick={() => downloadQRCode(seed.varietyCode)}
                            className="action-button qr"
                            style={{ border: 'none', cursor: 'pointer', background: '#e3f2fd', color: '#1976d2' }}
                            title="Download QR Code"
                          >
                            📥 QR
                          </button>
                          {!isReadOnly && (
                            <button
                              onClick={() => handleDelete(seed.id, seed.isActive)}
                              className="action-button delete"
                              style={{
                                border: 'none',
                                cursor: 'pointer',
                                background: seed.isActive ? '#ffebee' : '#e8f5e9',
                                color: seed.isActive ? '#d32f2f' : '#2e7d32'
                              }}
                            >
                              {seed.isActive ? '🗑️ Inactive' : '↻ Restore'}
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        {/* Form View */}
        {currentView === 'form' && (
          <div className="upload-form" style={{ marginBottom: '30px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2>
                {isCreating ? 'Add New Seed' : isReadOnly ? `View Seed: ${editingSeed?.varietyCode}` : `Edit Seed: ${editingSeed?.varietyCode}`}
              </h2>
              <button
                onClick={backToList}
                className="upload-button"
                style={{ background: '#666' }}
              >
                ← Back to List
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <h3 style={{ color: '#2d5016', marginBottom: '15px' }}>Basic Information</h3>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="varietyCode">Variety Code: *</label>
                  <input
                    type="text"
                    id="varietyCode"
                    name="varietyCode"
                    value={formData.varietyCode}
                    onChange={handleInputChange}
                    disabled={uploading || !isCreating || isReadOnly}
                    required
                    readOnly={isReadOnly}
                  />
                  {!isCreating && !isReadOnly && <small style={{ color: '#666' }}>Cannot be changed</small>}
                </div>

                <div className="form-group">
                  <label htmlFor="cropName">Crop Name: *</label>
                  <input
                    type="text"
                    id="cropName"
                    name="cropName"
                    value={formData.cropName}
                    onChange={handleInputChange}
                    disabled={uploading || !isCreating || isReadOnly}
                    required
                    readOnly={isReadOnly}
                  />
                  {!isCreating && !isReadOnly && <small style={{ color: '#666' }}>Cannot be changed</small>}
                </div>
              </div>

              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input
                    type="checkbox"
                    name="isActive"
                    checked={formData.isActive}
                    onChange={handleInputChange}
                    disabled={uploading || isReadOnly}
                    style={{ margin: 0 }}
                  />
                  <span>Active Seed</span>
                </label>
              </div>

              <div style={{ marginBottom: '20px', marginTop: '30px' }}>
                <h3 style={{ color: '#2d5016', margin: 0 }}>
                  Multilingual Information
                </h3>
                <small style={{ display: 'block', fontWeight: 'normal', color: '#666', fontSize: '14px', marginTop: '5px' }}>
                  Enter translations for all languages below. Use the Translation button in the top menu to run batch translation for all seeds.
                </small>
              </div>

              <TranslationPanel
                field="cropNameMl"
                fieldLabel="Crop Name"
                translations={translations.cropNameMl}
                onTranslationChange={handleTranslationChange}
                showAllLanguages={true}
                disabled={isReadOnly}
              />

              <TranslationPanel
                field="varietyNameMl"
                fieldLabel="Variety Name"
                translations={translations.varietyNameMl}
                onTranslationChange={handleTranslationChange}
                showAllLanguages={true}
                disabled={isReadOnly}
              />

              <TranslationPanel
                field="eligibleStatesMl"
                fieldLabel="Eligible States"
                translations={translations.eligibleStatesMl}
                onTranslationChange={handleTranslationChange}
                showAllLanguages={true}
                disabled={isReadOnly}
              />

              <TranslationPanel
                field="landPreparationMl"
                fieldLabel="Land Preparation"
                translations={translations.landPreparationMl}
                onTranslationChange={handleTranslationChange}
                showAllLanguages={true}
                disabled={isReadOnly}
              />

              <TranslationPanel
                field="seedProcessingMl"
                fieldLabel="Seed Processing"
                translations={translations.seedProcessingMl}
                onTranslationChange={handleTranslationChange}
                showAllLanguages={true}
                disabled={isReadOnly}
              />

              <TranslationPanel
                field="sowingTimeMl"
                fieldLabel="Sowing Time"
                translations={translations.sowingTimeMl}
                onTranslationChange={handleTranslationChange}
                showAllLanguages={true}
                disabled={isReadOnly}
              />

              <TranslationPanel
                field="seedRateMl"
                fieldLabel="Seed Rate"
                translations={translations.seedRateMl}
                onTranslationChange={handleTranslationChange}
                showAllLanguages={true}
                disabled={isReadOnly}
              />

              <TranslationPanel
                field="fertilizersMl"
                fieldLabel="Fertilizers"
                translations={translations.fertilizersMl}
                onTranslationChange={handleTranslationChange}
                showAllLanguages={true}
                disabled={isReadOnly}
              />

              <TranslationPanel
                field="weedControlMl"
                fieldLabel="Weed Control"
                translations={translations.weedControlMl}
                onTranslationChange={handleTranslationChange}
                showAllLanguages={true}
                disabled={isReadOnly}
              />

              <TranslationPanel
                field="irrigationMl"
                fieldLabel="Irrigation"
                translations={translations.irrigationMl}
                onTranslationChange={handleTranslationChange}
                showAllLanguages={true}
                disabled={isReadOnly}
              />

              <TranslationPanel
                field="harvestingMl"
                fieldLabel="Harvesting"
                translations={translations.harvestingMl}
                onTranslationChange={handleTranslationChange}
                showAllLanguages={true}
                disabled={isReadOnly}
              />

              <h3 style={{ color: '#2d5016', marginBottom: '15px', marginTop: '30px' }}>Seed Pouch Image</h3>

              {!isReadOnly && (
                <>
                  <div className="form-group">
                    <label htmlFor="imageFile">Upload Image (Optional):</label>
                    <input
                      type="file"
                      id="imageFile"
                      accept="image/jpeg,image/jpg,image/png,image/webp"
                      onChange={handleFileSelect}
                      disabled={uploading}
                    />
                    <small className="file-info">
                      Supported formats: JPG, PNG, WebP (Max size: 5MB)
                    </small>
                  </div>

                  {selectedFile && (
                    <div className="file-preview">
                      <h4>Selected File:</h4>
                      <p><strong>Name:</strong> {selectedFile.name}</p>
                      <p><strong>Size:</strong> {(selectedFile.size / 1024 / 1024).toFixed(2)} MB</p>
                      <div className="preview-image">
                        <img
                          src={URL.createObjectURL(selectedFile)}
                          alt="Preview"
                          style={{ maxWidth: '200px', maxHeight: '200px', objectFit: 'contain' }}
                        />
                      </div>
                    </div>
                  )}
                </>
              )}

              {editingSeed && editingSeed.pouchImageName && (
                <div className="current-image">
                  <h4>Current Image:</h4>
                  <img
                    src={getImageUrl(editingSeed.pouchImageName)}
                    alt={`${editingSeed.cropName} pouch`}
                    style={{ maxWidth: '200px', maxHeight: '200px', objectFit: 'contain' }}
                  />
                </div>
              )}

              {/* QR Code Preview for editing */}
              {editingSeed && qrPreviewUrl && (
                <div className="current-image" style={{ marginTop: '20px' }}>
                  <h4>QR Code Preview:</h4>
                  <img
                    src={qrPreviewUrl}
                    alt={`QR Code for ${editingSeed.varietyCode}`}
                    style={{ maxWidth: '200px', maxHeight: '200px', objectFit: 'contain', border: '1px solid #ddd', padding: '10px', background: 'white' }}
                  />
                  <div style={{ marginTop: '10px' }}>
                    <a
                      href={`/variety/${encodeURIComponent(editingSeed.varietyCode)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: '#2d5016', fontSize: '14px' }}
                    >
                      🔗 Preview Variety Page
                    </a>
                  </div>
                </div>
              )}

              {!isReadOnly && (
                <div className="form-actions" style={{ marginTop: '30px' }}>
                  <button
                    type="submit"
                    className="upload-button"
                    disabled={uploading || !formData.cropName || !formData.varietyCode}
                  >
                    {uploading ? '⏳ Saving...' : (isCreating ? '✅ Create Seed' : '✅ Update Seed')}
                  </button>

                  <button
                    type="button"
                    className="upload-button"
                    onClick={cancelForm}
                    disabled={uploading}
                    style={{ marginLeft: '10px', background: '#666' }}
                  >
                    Cancel
                  </button>
                </div>
              )}

              {formMessage && (
                <div
                  className={`message ${formMessage.includes('success') || formMessage.includes('✅') ? 'success' : 'error'}`}
                  style={{ marginTop: '20px' }}
                >
                  {formMessage}
                </div>
              )}
            </form>
          </div>
        )}
      </div>

      {/* Floating Scroll Controls */}
      <div className="fixed right-5 bottom-5 flex flex-col gap-2 z-50">
        {/* Help tooltip */}
        <button
          onClick={() => setShowScrollHelp(!showScrollHelp)}
          title="Show keyboard shortcuts"
          className={`w-12 h-12 rounded-full border border-namdhari-green/30 ${showScrollHelp ? 'bg-namdhari-light-green/95' : 'bg-namdhari-light-green/70'
            } text-namdhari-green text-xl cursor-pointer shadow-soft flex items-center justify-center transition-all duration-200 font-bold backdrop-blur-sm hover:bg-namdhari-light-green/95 hover:shadow-medium`}
        >
          ?
        </button>

        {showScrollHelp && (
          <div className="absolute right-14 bottom-0 bg-namdhari-mint/98 border border-namdhari-green/30 rounded-lg p-4 shadow-medium min-w-[280px] text-sm backdrop-blur-md">
            <div className="font-bold mb-2 text-namdhari-green">
              Keyboard Shortcuts
            </div>
            <div className="flex flex-col gap-1.5">
              <div className="flex justify-between">
                <span className="font-medium">Ctrl + Home:</span>
                <span>Jump to Top</span>
              </div>
              <div className="flex justify-between">
                <span className="font-medium">Ctrl + End:</span>
                <span>Jump to Bottom</span>
              </div>
              <div className="flex justify-between">
                <span className="font-medium">Page Up:</span>
                <span>Scroll Up</span>
              </div>
              <div className="flex justify-between">
                <span className="font-medium">Page Down:</span>
                <span>Scroll Down</span>
              </div>
            </div>
            <div className="mt-2 text-xs text-gray-600 italic">
              Or use the buttons →
            </div>
          </div>
        )}

        {/* Translation Management Modal */}
        {showTranslationModal && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000
            }}
            onClick={() => setShowTranslationModal(false)}
          >
            <div
              style={{
                backgroundColor: 'white',
                padding: '30px',
                borderRadius: '8px',
                maxWidth: '600px',
                width: '90%',
                maxHeight: '80vh',
                overflow: 'auto'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <h2 style={{ marginTop: 0, color: '#2d5016' }}>Translation Management</h2>

              <p style={{ color: '#666', marginBottom: '20px' }}>
                Manage the translation batch process for all seeds in the database.
              </p>

              {/* Service Status Indicator */}
              {isCheckingStatus && (
                <div style={{
                  padding: '12px',
                  background: '#e3f2fd',
                  borderRadius: '4px',
                  marginBottom: '15px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px'
                }}>
                  <span>⏳</span>
                  <span>Checking service status...</span>
                </div>
              )}

              {!isCheckingStatus && isServiceRunning && serviceState.activeState === 'active' && (
                <div style={{
                  padding: '12px',
                  background: '#e8f5e9',
                  borderRadius: '4px',
                  marginBottom: '15px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  border: '1px solid #4caf50'
                }}>
                  <span>✅</span>
                  <div>
                    <div style={{ fontWeight: 'bold', color: '#2e7d32' }}>Translation service is running</div>
                    <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                      State: {serviceState.activeState} ({serviceState.subState})
                    </div>
                  </div>
                </div>
              )}

              {!isCheckingStatus && isServiceRunning && serviceState.activeState === 'activating' && (
                <div style={{
                  padding: '12px',
                  background: '#e3f2fd',
                  borderRadius: '4px',
                  marginBottom: '15px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  border: '1px solid #2196f3'
                }}>
                  <span>🔄</span>
                  <div>
                    <div style={{ fontWeight: 'bold', color: '#1976d2' }}>Translation service is starting...</div>
                    <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                      State: {serviceState.activeState} ({serviceState.subState})
                    </div>
                  </div>
                </div>
              )}

              {!isCheckingStatus && !isServiceRunning && serviceState.activeState === 'failed' && (
                <div style={{
                  padding: '12px',
                  background: '#ffebee',
                  borderRadius: '4px',
                  marginBottom: '15px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  border: '1px solid #f44336'
                }}>
                  <span>❌</span>
                  <div>
                    <div style={{ fontWeight: 'bold', color: '#c62828' }}>Translation service failed</div>
                    <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                      State: {serviceState.activeState} ({serviceState.subState})
                    </div>
                  </div>
                </div>
              )}

              {!isCheckingStatus && !isServiceRunning && serviceState.activeState !== 'failed' && (
                <div style={{
                  padding: '12px',
                  background: '#fff3e0',
                  borderRadius: '4px',
                  marginBottom: '15px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  border: '1px solid #ff9800'
                }}>
                  <span>⚪</span>
                  <div>
                    <div style={{ color: '#e65100' }}>Translation service is not running</div>
                    <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                      State: {serviceState.activeState} ({serviceState.subState})
                    </div>
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
                <button
                  onClick={handleStartTranslation}
                  disabled={isServiceRunning}
                  className="upload-button"
                  style={{
                    background: '#2d5016',
                    flex: 1,
                    opacity: isServiceRunning ? 0.5 : 1,
                    cursor: isServiceRunning ? 'not-allowed' : 'pointer'
                  }}
                  title={isServiceRunning ? 'Service is already running' : 'Start translation batch process'}
                >
                  🚀 Start Translation
                </button>

                <button
                  onClick={handleCheckTranslationStatus}
                  disabled={isCheckingStatus}
                  className="upload-button"
                  style={{
                    background: '#1976d2',
                    flex: 1,
                    opacity: isCheckingStatus ? 0.5 : 1
                  }}
                >
                  {isCheckingStatus ? '⏳ Checking...' : '🔄 Refresh Status'}
                </button>
              </div>

              {translationStatus && (
                <div
                  style={{
                    padding: '15px',
                    background: translationStatus.includes('✅') ? '#e8f5e9' : translationStatus.includes('⚠️') ? '#fff3e0' : '#f5f5f5',
                    borderRadius: '4px',
                    marginBottom: '20px',
                    whiteSpace: 'pre-wrap',
                    fontFamily: 'monospace',
                    fontSize: '12px',
                    maxHeight: '300px',
                    overflow: 'auto'
                  }}
                >
                  {translationStatus}
                </div>
              )}

              <button
                onClick={() => setShowTranslationModal(false)}
                className="cancel-button"
                style={{ width: '100%' }}
              >
                Close
              </button>
            </div>
          </div>
        )}

        {/* Utils Panel Modal */}
        {showUtilsModal && (
          <UtilsPanel onClose={() => setShowUtilsModal(false)} />
        )}

        <button
          onClick={scrollToTop}
          title="Scroll to Top (Ctrl + Home)"
          className="w-12 h-12 rounded-full border border-namdhari-green/30 bg-namdhari-pale-green/70 text-black text-3xl cursor-pointer shadow-soft flex items-center justify-center transition-all duration-200 backdrop-blur-sm hover:bg-namdhari-pale-green/95 hover:shadow-medium hover:scale-105"
        >
          ⬆
        </button>

        <button
          onClick={scrollUp}
          title="Scroll Up (Page Up)"
          className="w-12 h-12 rounded-full border border-namdhari-green/30 bg-namdhari-light-green/70 text-namdhari-green text-xl cursor-pointer shadow-soft flex items-center justify-center transition-all duration-200 backdrop-blur-sm hover:bg-namdhari-light-green/95 hover:shadow-medium hover:scale-105"
        >
          ▲
        </button>

        <button
          onClick={scrollDown}
          title="Scroll Down (Page Down)"
          className="w-12 h-12 rounded-full border border-namdhari-green/30 bg-namdhari-light-green/70 text-namdhari-green text-xl cursor-pointer shadow-soft flex items-center justify-center transition-all duration-200 backdrop-blur-sm hover:bg-namdhari-light-green/95 hover:shadow-medium hover:scale-105"
        >
          ▼
        </button>

        <button
          onClick={scrollToBottom}
          title="Scroll to Bottom (Ctrl + End)"
          className="w-12 h-12 rounded-full border border-namdhari-green/30 bg-namdhari-pale-green/70 text-black text-3xl cursor-pointer shadow-soft flex items-center justify-center transition-all duration-200 backdrop-blur-sm hover:bg-namdhari-pale-green/95 hover:shadow-medium hover:scale-105"
        >
          ⬇
        </button>
      </div>
    </div>
  );
};

export default SeedManager;
